import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Logo } from '../components/layout';
import { FullPageLoader } from '../components/common';

const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  
  const { currentUser, isLoading, signIn } = useAuth();
  const navigate = useNavigate();

  // Redirect if already logged in
  useEffect(() => {
    if (currentUser && !isLoading) {
      navigate('/');
    }
  }, [currentUser, isLoading, navigate]);

  const handleAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsProcessing(true);
    setError('');

    try {
      await signIn(email, password);
      navigate('/');
    } catch (err: any) {
      console.error('Auth Error:', err);
      setError(err.message || 'Authentication failed. Please check your credentials.');
    } finally {
      setIsProcessing(false);
    }
  };

  if (isLoading) {
    return <FullPageLoader message="Establishing Cloud Session..." />;
  }

  return (
    <div className="min-h-[100dvh] flex items-center justify-center bg-slate-50 p-6">
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-10 flex flex-col items-center">
          <div className="relative mb-6">
            <div className="absolute inset-0 bg-indigo-500/20 blur-2xl rounded-full scale-150 animate-pulse" />
            <Logo size={80} className="relative z-10 drop-shadow-2xl" variant="gradient" />
          </div>
          <h1 className="text-3xl font-black text-slate-900 tracking-tight">OB WorkFlow</h1>
          <p className="text-slate-500 mt-2 font-medium">Enterprise Orchestration Gateway</p>
        </div>

        {/* Login Form */}
        <div className="bg-white p-8 md:p-10 rounded-[2.5rem] shadow-2xl border border-slate-200">
          <div className="mb-8 text-center">
            <h2 className="text-xl font-black text-slate-800">Secure Sign In</h2>
            <p className="text-slate-400 text-xs font-bold uppercase tracking-widest mt-1">Authorized Access Only</p>
          </div>

          <form onSubmit={handleAuth} className="space-y-6">
            {error && (
              <div className="p-4 bg-rose-50 text-rose-600 text-sm font-bold rounded-2xl border border-rose-100 flex flex-col gap-1">
                <div className="flex items-center gap-3">
                  <svg className="w-5 h-5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                  </svg>
                  <span>Authentication Issue</span>
                </div>
                <p className="text-xs font-medium ml-8 opacity-80">{error}</p>
              </div>
            )}
            
            <div className="relative">
              <label className="block text-[11px] font-black uppercase tracking-widest text-slate-400 mb-2 ml-1">
                Email Address
              </label>
              <input 
                type="email" 
                inputMode="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-5 py-4 bg-slate-50/50 rounded-2xl border border-slate-200 focus:bg-white focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10 outline-none transition-all placeholder-slate-400 text-slate-900 font-bold"
                placeholder="you@organization.com"
                required
              />
            </div>

            <div className="relative">
              <label className="block text-[11px] font-black uppercase tracking-widest text-slate-400 mb-2 ml-1">
                Password
              </label>
              <input 
                type="password" 
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-5 py-4 bg-slate-50/50 rounded-2xl border border-slate-200 focus:bg-white focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10 outline-none transition-all placeholder-slate-400 text-slate-900 font-bold"
                placeholder="••••••••"
                required
              />
            </div>

            <button 
              type="submit"
              disabled={isProcessing}
              className="w-full bg-indigo-600 text-white font-black uppercase tracking-widest text-[11px] py-4 px-4 rounded-2xl hover:bg-indigo-700 transition-all shadow-xl shadow-indigo-500/20 active:scale-[0.98] disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {isProcessing ? (
                <>
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Authenticating...
                </>
              ) : 'Initialize Session'}
            </button>
          </form>

          <div className="mt-8 pt-8 border-t border-slate-100">
            <p className="text-center text-[10px] text-slate-400 font-medium italic">
              Self-registration is disabled. Access is limited to invited members via the Administrator dashboard.
            </p>
          </div>
        </div>
        
        <p className="text-center mt-8 text-xs text-slate-400 font-medium">
          Encrypted Cloud Handshake powered by Supabase Auth
        </p>
      </div>
    </div>
  );
};

export default Login;
